import { useMemo, useState, useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import SingleDatePicker from "./SingleDatePicker";
import Banner from "@/components/Banner";
import { useMutation } from "@apollo/client";
import { UPDATE_STAGED_PARTICIPATIONS } from "../../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { ActivityParticipationRole } from "@/pages/activities/activity-types/types";
import { CreditRate } from "@/pages/activities/activity-types/types";
import { getParticipationLevelOptions, getParticipationRoleOptions, isoToLocalDate } from "../_utils";
import { AttendanceType } from "@/pages/activities/activity-types/types";
import { map, uniq } from "lodash";
import PersonAutocomplete from "./PersonAutocomplete";
import { validateDate } from "../_utils";

export interface SelectedParticipant {
  id: string;
  pgaId: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  attendanceType?: AttendanceType;
  participationRole?: ActivityParticipationRole;
}

type EditParticipationFormValues = {
  participationLevel: AttendanceType | undefined;
  participationRole: ActivityParticipationRole | undefined;
  startDate: string;
  endDate: string;
  personSearch: string;
  selectedPgaId: string;
};

export default function EditParticipationModal({
  onDismiss,
  selectedParticipants,
  selectedParticipantIds,
  onSuccess,
  creditRates = [],
  allowParticipationStarting,
  allowParticipationThrough
}: {
  onDismiss: () => void;
  selectedParticipants: SelectedParticipant[];
  selectedParticipantIds: string[];
  onSuccess?: (details: { participantCount: number; fieldsChanged: string[] }) => void | Promise<unknown>;
  creditRates?: CreditRate[];
  allowParticipationStarting?: string | null;
  allowParticipationThrough?: string | null;
}) {
  const selectedParticipant = selectedParticipants.length > 0 ? selectedParticipants[0] : undefined;

  const commonValues = useMemo(() => {
    const allSame = <T,>(getter: (p: SelectedParticipant) => T): T | undefined => {
      const values = map(selectedParticipants, getter);
      const unique = uniq(values);
      return unique.length === 1 ? unique[0] : undefined;
    };

    return {
      attendanceType: allSame((p) => p.attendanceType),
      participationRole: allSame((p) => p.participationRole),
      startDate: allSame((p) => p.startDate),
      endDate: allSame((p) => p.endDate)
    };
  }, [selectedParticipants]);

  const defaultValues = useMemo<EditParticipationFormValues>(() => {
    const startStr = commonValues.startDate?.split("T")[0]?.trim() || "";
    const endStr = commonValues.endDate?.split("T")[0]?.trim() || "";
    return {
      participationLevel: commonValues.attendanceType,
      participationRole: commonValues.participationRole,
      startDate: startStr,
      endDate: endStr,
      personSearch: selectedParticipant?.name && selectedParticipant.name !== "--" ? selectedParticipant.name : "",
      selectedPgaId: selectedParticipant?.pgaId ?? ""
    };
  }, [commonValues, selectedParticipant]);

  const form = useForm<EditParticipationFormValues>({
    defaultValues,
    values: defaultValues
  });

  const { control, watch, setValue, formState } = form;
  const participationLevel = watch("participationLevel");
  const participationRole = watch("participationRole");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const selectedPgaId = watch("selectedPgaId");

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setValue("endDate", startDate);
    }
  }, [startDate, endDate, setValue]);

  const { Modal, close } = useModal();
  const [bannerError, setBannerError] = useState("");
  const saveDetailsRef = useRef<{ participantCount: number; fieldsChanged: string[] } | null>(null);

  const [updateStagedParticipations, { loading }] = useMutation(UPDATE_STAGED_PARTICIPATIONS, {
    onCompleted: async (data) => {
      const result = data?.updateStagedParticipations;
      if (result?.success) {
        const details = saveDetailsRef.current ?? {
          participantCount: selectedParticipantIds.length,
          fieldsChanged: []
        };
        saveDetailsRef.current = null;
        await onSuccess?.(details);
        showSnackbar(result.message ?? "Participation updated successfully", { close: true });
        close();
        onDismiss();
      } else {
        setBannerError(result?.message ?? "Failed to update participation.");
      }
    },
    onError: (error) => {
      setBannerError(error.message ?? "Failed to update participation.");
    }
  });

  const handleSave = async () => {
    setBannerError("");
    const { dirtyFields } = formState;
    const fieldsChanged: string[] = [];
    if (dirtyFields.participationLevel) fieldsChanged.push("attendanceType");
    if (dirtyFields.participationRole) fieldsChanged.push("participationRole");
    if (dirtyFields.selectedPgaId || dirtyFields.personSearch) fieldsChanged.push("pgaId");
    if (dirtyFields.startDate) fieldsChanged.push("startDate");
    if (dirtyFields.endDate) fieldsChanged.push("endDate");

    const saveDetails = { participantCount: selectedParticipantIds.length, fieldsChanged };
    saveDetailsRef.current = saveDetails;

    await updateStagedParticipations({
      variables: {
        input: {
          participations: selectedParticipantIds.map((id) => ({
            id,
            ...(participationLevel && { attendanceType: participationLevel }),
            ...(participationRole && { participationRole }),
            ...(selectedParticipantIds.length === 1 && selectedPgaId && { pgaId: selectedPgaId }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate })
          }))
        }
      }
    });
  };

  return (
    <Modal
      onClosed={onDismiss}
      actions={
        <>
          <Button variant="text" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={loading || (selectedParticipantIds.length === 1 && !selectedPgaId)}
            data-testid="edit-participation-save-button"
          >
            Save
          </Button>
        </>
      }
      headline="Edit Participation"
      displayOverflow
      className="md:w-[520px]"
    >
      <FormProvider {...form}>
        <div className="space-y-6">
          {!!bannerError && (
            <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
          )}
          {selectedParticipantIds.length === 1 && (
            <Controller
              name="personSearch"
              control={control}
              render={({ field: { value, onChange } }) => (
                <PersonAutocomplete
                  label="Person"
                  required
                  query={value}
                  onChangeText={onChange}
                  reset={() => {
                    const name =
                      selectedParticipant?.name && selectedParticipant.name !== "--" ? selectedParticipant.name : "";
                    onChange(name);
                    setValue("selectedPgaId", selectedParticipant?.pgaId ?? "");
                  }}
                  onSelect={(person) => {
                    setValue("selectedPgaId", person.pgaId || "");
                    onChange(`${person.firstName} ${person.lastName}`);
                  }}
                />
              )}
            />
          )}
          {getParticipationLevelOptions(creditRates).length > 1 && (
            <Controller
              name="participationLevel"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Attendance Type"
                  required
                  options={getParticipationLevelOptions(creditRates)}
                  value={value ?? undefined}
                  onSelect={(v) => onChange(v as AttendanceType)}
                />
              )}
            />
          )}

          {getParticipationRoleOptions(creditRates).length > 1 && (
            <Controller
              name="participationRole"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Participation Role"
                  required
                  options={getParticipationRoleOptions(creditRates)}
                  value={value ?? undefined}
                  onSelect={(v) => onChange(v as ActivityParticipationRole)}
                />
              )}
            />
          )}

          <div className="flex gap-2">
            <SingleDatePicker<EditParticipationFormValues>
              control={control}
              name="startDate"
              label="Start Date"
              required
              useModal
              minDate={isoToLocalDate(allowParticipationStarting)}
              maxDate={isoToLocalDate(allowParticipationThrough, true)}
              rules={{
                validate: (value) =>
                  validateDate(value, "Start Date", allowParticipationStarting, allowParticipationThrough)
              }}
            />
            <SingleDatePicker<EditParticipationFormValues>
              control={control}
              name="endDate"
              label="End Date"
              required
              useModal
              minDate={isoToLocalDate(startDate)}
              maxDate={isoToLocalDate(allowParticipationThrough, true)}
              rules={{
                validate: (value) =>
                  validateDate(value, "End Date", allowParticipationStarting, allowParticipationThrough)
              }}
            />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
}
